/* eslint-disable consistent-return */
import get from 'lodash/get';
import { formFieldValidators } from '../form-field-validators';
import {
    validateOldPassword,
    validateConfirmPassword,
    validateCountry,
    validateConfirmEmail,
    validateCity,
    validateAddressLine1,
    validateAddressLine2,
    validateAddressLine3,
    validatePostcode,
    validateMobilePhone,
    validateCaptchaBox,
    validateMastercard
} from '../../../../validators/text-validators';
import FormValidator from '../form-validator';
import { validateDate } from '../../../../validators/date-validators';
import { isFormField, isEmpty } from '../../../utils/obj-utils';
import { formMessageConstants } from '../../../utils/constants/form-message-constants';
import { formConstants } from '../../../utils/constants/form-constants';
import { createDate } from '../../../utils/date-utils';
import { hasValue } from '../../../../../../general/js/general-helpers';

// eslint-disable-next-line
export const handleFieldChange = (eventTarget) => (state) => {
    const {
        checked,
        name,
        value,
        type
    } = eventTarget;
    const valueToUpdate = type === 'checkbox' ? checked : value;

    return { [name]: valueToUpdate };
};

export const handleFieldChangeWithValidation = (eventTarget, fieldData) => (state) => {
    const {
        checked,
        name,
        value,
        type
    } = eventTarget;

    const valueToUpdate = type === 'checkbox' ? checked : value;

    if (!isEmpty(fieldData)) {
        const { isHidden, isOptional } = fieldData;
        if (isHidden) {
            return { ...state, [name]: valueToUpdate };
        }

        if (isOptional && isEmpty(valueToUpdate)) {
            return { ...state, [name]: valueToUpdate };
        }
    }

    const { errors } = state;
    const fieldValidator = formFieldValidators[name];

    return {
        ...state,
        [name]: valueToUpdate,
        errors: {
            ...errors,
            [name]: fieldValidator !== undefined ? fieldValidator(valueToUpdate) : { isValid: false, errorMessage: formMessageConstants.somethingWentWrong }
        }
    };
};

export const handleConfirmEmailFieldChange = (eventTarget) => (state) => {
    const { emailAddress, errors } = state;
    const { name, value } = eventTarget;
    return {
        ...state,
        [name]: value,
        errors: {
            ...errors,
            confirmEmailAddress: validateConfirmEmail(emailAddress, value)
        }
    };
};

export const handleOldPasswordFieldChange = (eventTarget) => (state) => {
    const { password, errors } = state;
    const { name, value } = eventTarget;
    return {
        ...state,
        [name]: value,
        errors: {
            ...errors,
            oldPassword: validateOldPassword(password, value)
        }
    };
};

export const handleConfirmPasswordFieldChange = (eventTarget) => (state) => {
    const { password, errors } = state;
    const { name, value } = eventTarget;
    return {
        ...state,
        [name]: value,
        errors: {
            ...errors,
            confirmPassword: validateConfirmPassword(password, value)
        }
    };
};

export const handleDateChange = (event) => (state) => {
    const { errors } = state;
    const validationResult = validateDate(event);
    let dateOfBirth;

    if (validationResult.isValid) {
        const { day, month, year } = event;
        const dayAsNumber = parseInt(day, 10);
        const monthAsNumber = parseInt(month, 10);
        const yearAsNumber = parseInt(year, 10);
        dateOfBirth = createDate(yearAsNumber, monthAsNumber, dayAsNumber);
    }

    return {
        ...state,
        dateOfBirth,
        errors: {
            ...errors,
            dateOfBirth: validationResult
        }
    };
};

export const handleMobilePhoneChange = (event, isOptional) => (state) => {
    const { errors } = state;
    const validationResult = validateMobilePhone(event.phoneNumber, isOptional);

    return {
        ...state,
        mobilePhone: {
            phoneNumber: event.phoneNumber,
            selectedCountry: event.selectedCountry
        },
        errors: {
            ...errors,
            mobilePhone: validationResult
        }
    };
};

export const handleCaptchaChange = (token) => (state) => {
    const isChecked = !!token;
    const { errors } = state;

    const newState = {
        ...state,
        [formConstants.reCaptcha]: token,
        errors: {
            ...errors,
            [formConstants.reCaptcha]: validateCaptchaBox(isChecked)
        }
    };

    return newState;
};

export const handleMastercardNumberChange = (event, mastercardRegex, mastercardError) => (state) => {
    const { errors } = state;
    const validationResult = validateMastercard(event.value, mastercardRegex, mastercardError);

    return {
        ...state,
        [formConstants.mastercard]: event.value,
        errors: {
            ...errors,
            [formConstants.mastercard]: validationResult
        }
    };
};

export const handleCountryChange = (eventTarget, county, stateField = null) => (state) => {
    const { name, value } = eventTarget;
    const { errors } = state;

    const availableCounties = county.options.filter((c) => c.countryCode === value);
    const availableStates = hasValue(stateField) ? stateField.options.filter((s) => s.countryCode === value) : [];

    return {
        ...state,
        availableCounties,
        availableStates,
        county: formMessageConstants.emptyString,
        state: formMessageConstants.emptyString,
        [name]: value,
        [formConstants.city]: formMessageConstants.emptyString,
        [formConstants.addressLine1]: formMessageConstants.emptyString,
        [formConstants.addressLine2]: formMessageConstants.emptyString,
        [formConstants.addressLine3]: formMessageConstants.emptyString,
        [formConstants.postcode]: formMessageConstants.emptyString,
        errors: {
            ...errors,
            [name]: validateCountry(value)
        }
    };
};

export const handleAddressDataChange = (retrievedUserAddress, countyField, stateField = null) => (state) => {
    const { errors, country, county } = state;
    const stateValue = get(state, 'state', formMessageConstants.emptyString);
    const retrievedCountry = retrievedUserAddress[formConstants.country] || country;
    const availableCounties = isEmpty(retrievedCountry) ? [] : countyField.options.filter((c) => c.countryCode === retrievedCountry);
    const availableStates = isEmpty(retrievedCountry) ? [] : stateField.options.filter((s) => s.countryCode === retrievedCountry);
    const retrievedCounty = retrievedUserAddress[formConstants.county];
    const retrievedState = retrievedUserAddress[formConstants.state];
    const availableCounty = availableCounties.find((c) => c.label.includes(retrievedCounty));
    const availableState = availableStates.find((s) => s.label.includes(retrievedState));
    const selectedCounty = isEmpty(availableCounty) ? county : availableCounty.label;
    const selectedState = isEmpty(availableState) ? stateValue : availableState.label;

    return {
        ...state,
        availableCounties,
        availableStates,
        [formConstants.country]: retrievedCountry,
        [formConstants.county]: selectedCounty,
        [formConstants.state]: selectedState,
        [formConstants.city]: retrievedUserAddress[formConstants.city],
        [formConstants.addressLine1]: retrievedUserAddress[formConstants.addressLine1],
        [formConstants.addressLine2]: retrievedUserAddress[formConstants.addressLine2],
        [formConstants.addressLine3]: retrievedUserAddress[formConstants.addressLine3],
        [formConstants.postcode]: retrievedUserAddress[formConstants.postcode],
        errors: {
            ...errors,
            country: validateCountry(retrievedCountry),
            city: validateCity(retrievedUserAddress[formConstants.city]),
            addressLine1: validateAddressLine1(retrievedUserAddress[formConstants.addressLine1]),
            addressLine2: validateAddressLine2(retrievedUserAddress[formConstants.addressLine2]),
            addressLine3: validateAddressLine3(retrievedUserAddress[formConstants.addressLine3]),
            postcode: validatePostcode(retrievedUserAddress[formConstants.postcode])
        }
    };
};

export const handleFieldValidState = (fieldName, errors) => {
    if (!errors) {
        return { isValid: true };
    }

    return errors[fieldName] !== undefined ? errors[fieldName] : { isValid: true };
};

export const handleFormValidation = (formData) => (state) => {
    const formDataKeys = Object.keys(formData);
    const formValues = {};

    formDataKeys.forEach((formDataKey) => {
        formValues[formDataKey] = state[formDataKey];
    });

    const validationResult = FormValidator(formData, formValues);

    return {
        ...state,
        errors: validationResult.errors,
        formHasErrors: validationResult.formHasErrors
    };
};

export const updateFormFields = (formData) => (state) => {
    const formDataKeys = Object.keys(formData);
    const { county, country } = formData;
    const stateField = get(formData, 'state', null);
    const availableCounties = county.options.filter((c) => c.countryCode === country.defaultValue);
    const availableStates = hasValue(stateField) ? stateField.options.filter((s) => s.countryCode === country.defaultValue) : [];

    const updatedState = {
        ...state,
        availableCounties,
        availableStates,
        formHasErrors: false
    };

    formDataKeys.forEach((formDataKey) => {
        if (isFormField(formData[formDataKey])) {
            updatedState[formDataKey] = formData[formDataKey].defaultValue;
        } else {
            updatedState[formDataKey] = formData[formDataKey];
        }
    });

    if (!isEmpty(formData[formConstants.offersDetails])) {
        const offersDetailsDefaultValue = formData[formConstants.offersDetails].defaultValue;
        updatedState[formConstants.refuseOffers] = offersDetailsDefaultValue[formConstants.refuseOffers];
        updatedState[formConstants.acceptOffers] = offersDetailsDefaultValue[formConstants.acceptOffers];
    }

    return updatedState;
};

export const updateAvailableCounties = (receivedCountry, county) => (state) => {
    if (hasValue(receivedCountry) && hasValue(county)) {
        const availableCounties = hasValue(county.options) ? county.options.filter((c) => c.countryCode === receivedCountry) : {};

        if (!hasValue(availableCounties)) return { ...state };

        return {
            ...state,
            availableCounties
        };
    }
};

export const updateAvailableStates = (receivedCountry, stateField) => (state) => {
    if (hasValue(receivedCountry) && hasValue(stateField)) {
        const availableStates = hasValue(stateField.options) ? stateField.options.filter((s) => s.countryCode === receivedCountry) : {};

        if (!hasValue(availableStates)) return { ...state };

        return {
            ...state,
            availableStates
        };
    }
};

export const updateUsername = (userName) => (state) => {
    return {
        ...state,
        userName,
        formHasErrors: false
    };
};

export const updateMembership = (membership) => (state) => {
    return {
        ...state,
        membership
    };
};

export const updateOneClubTermsAndConditions = (becomeOneClubMember) => (state) => {
    const { formData } = state;

    formData[formConstants.oneClubTermsAndServices].defaultValue = becomeOneClubMember;

    return {
        ...state,
        [formConstants.oneClubTermsAndServices]: becomeOneClubMember,
        formData
    };
};

export const updateActiveMemberships = (activeMemberships) => (state) => {
    return {
        ...state,
        activeMemberships
    };
};


export const updateFormData = (formData) => (state) => {
    return {
        ...state,
        formData
    };
};

export const updateUserAccountData = (userAccountData) => (state) => {
    return {
        ...state,
        userAccountData
    };
};

export const updateSavingState = (isSaving) => (state) => {
    return {
        ...state,
        isSaving,
        formHasErrors: false
    };
};

export const updateAddressesFormAccountData = (accountData) => (state) => {
    return {
        ...state,
        accountData
    };
};

export const updateOrderHistoryData = (orderHistoryData) => (state) => {
    return {
        ...state,
        orderHistoryData
    };
};

export const updateProcessingState = (isProcessing) => (state) => {
    return {
        ...state,
        isProcessing,
        formHasErrors: false
    };
};

export const updateVerificationCode = (verificationCode) => (state) => {
    return {
        ...state,
        verificationCode
    };
};

export const updateAddressesFormState = (formState, addressForUpdate) => (state) => {
    return {
        ...state,
        formState,
        addressForUpdate
    };
};

export const updateOrderHistoryDisplayFormState = (formState, orderHistoryForDisplay) => (state) => {
    return {
        ...state,
        formState,
        orderHistoryForDisplay,
        fileNumberForDisplay: 0
    };
};

export const updateFileSummaryFormState = (formState, orderHistoryForDisplay) => (state) => {
    return {
        ...state,
        formState,
        orderHistoryForDisplay
    };
};

export const updateFileNumberForDisplay = (fileNumberForDisplay) => (state) => {
    return {
        ...state,
        fileNumberForDisplay
    };
};

export const updateOrderHistoryActivePageNumber = (orderHistoryActivePageNumber) => (state) => {
    return {
        ...state,
        orderHistoryActivePageNumber
    };
};

export const toggleKey = (key) => (state) => {
    return {
        ...state,
        [key]: !state[key]
    };
};

export const handleSubmit = (isSubmitAction) => (state) => ({
    ...state,
    isSubmitAction
});

export const loadTextAndImageFields = (fields = []) => (state) => {
    if (!hasValue(fields)) {
        return {
            ...state
        };
    }

    const keyValuedFields = getKeyValuedTextAndImageFields(fields);

    return {
        ...state,
        ...keyValuedFields
    };
};

const getKeyValuedTextAndImageFields = (fields = []) => {
    let fieldCounter = 0;
    const keyValuedFields = {};

    if (!hasValue(fields)) {
        return keyValuedFields;
    }

    fields.forEach((field) => {
        const fieldKey = `${field.fieldKey}-${fieldCounter}`;
        fieldCounter++;

        if (hasValue(fieldKey)) {
            keyValuedFields[fieldKey] = field;
        }
    });

    return keyValuedFields;
};
